import { useState, useEffect } from 'react';
import MetricCard from '../components/cards/MetricCard';
import api from '../services/api';

export default function CognitiveMastery() {
  const [data, setData] = useState(null);
  const [atlasData, setAtlasData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('operational');

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const [cmResult, atlasResult] = await Promise.all([
          api.getCognitiveMastery(),
          api.getAtlasMetrics().catch(() => ({ data: null })),
        ]);
        setData(cmResult.data);
        setAtlasData(atlasResult.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error loading Cognitive Mastery data: {error}
        </div>
      </div>
    );
  }

  const summary = data?.summary;
  const nodes = data?.nodeBreakdown || [];
  const atlas = atlasData?.atlas || {};
  const aha = atlasData?.ahaMoment || {};

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-teacher-text-primary">Cognitive Mastery</h1>
        <p className="text-teacher-text-secondary">Atlas loop engagement, retrieval outcomes, and investor narrative</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-teacher-border mb-6">
        {['operational', 'investor', 'nodes'].map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              tab === t
                ? 'text-primary-700 border-primary-600'
                : 'text-teacher-text-secondary border-transparent hover:text-teacher-text-primary'
            }`}
          >
            {t === 'operational' ? 'Operational' : t === 'investor' ? 'Investor Story' : 'Node Breakdown'}
          </button>
        ))}
      </div>

      {/* Operational Tab */}
      {tab === 'operational' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
            <MetricCard title="Users Triggered" value={summary?.uniqueUsersTriggered} loading={loading} />
            <MetricCard title="Capsule Open Rate" value={summary?.capsuleOpenRate != null ? `${summary.capsuleOpenRate}%` : null} loading={loading} />
            <MetricCard title="Retrieval Pass Rate" value={summary?.retrievalPassRate != null ? `${summary.retrievalPassRate}%` : null} loading={loading} />
            <MetricCard title="Detection→Resolved" value={atlas.weak_spots_detected > 0 ? `${Math.round((atlas.nodes_mastered / atlas.weak_spots_detected) * 100)}%` : '—'} loading={loading} />
            <MetricCard title="Nodes Mastered" value={atlas.nodes_mastered ?? '—'} loading={loading} />
            <MetricCard title="Top Node" value={summary?.mostTriggeredNode ?? '—'} loading={loading} />
          </div>

          {/* Aha Moment Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-teacher-border">
              <h3 className="text-lg font-semibold text-teacher-text-primary mb-4">Aha Moment Tracking</h3>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-purple-700">{aha.users_reached_total ?? '—'}</div>
                  <div className="text-xs text-teacher-text-secondary">Users reached aha</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-700">
                    {aha.avg_time_to_aha_hours != null ? `${aha.avg_time_to_aha_hours}h` : '—'}
                  </div>
                  <div className="text-xs text-teacher-text-secondary">Avg time to aha</div>
                </div>
              </div>
              <div className="text-sm text-teacher-text-secondary">
                <div className="font-medium text-teacher-text-primary mb-2">Blocked Users</div>
                <div className="flex justify-between py-1">
                  <span>Insufficient practice (&lt;10 Qs)</span>
                  <span className="font-medium">{aha.blocked_users?.insufficient_practice ?? 0}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-teacher-border">
              <h3 className="text-lg font-semibold text-teacher-text-primary mb-4">Daily Atlas Activity</h3>
              <div className="space-y-3">
                {[
                  { label: 'Weak spots detected', value: atlas.weak_spots_detected },
                  { label: 'Capsules opened', value: atlas.capsules_opened },
                  { label: 'Capsules completed', value: atlas.capsules_completed },
                  { label: 'Retrieval attempts', value: atlas.retrieval_attempts },
                  { label: 'Retrieval passes', value: atlas.retrieval_passes },
                  { label: 'Nodes mastered', value: atlas.nodes_mastered },
                ].map(row => (
                  <div key={row.label} className="flex justify-between text-sm">
                    <span className="text-teacher-text-secondary">{row.label}</span>
                    <span className="font-semibold text-teacher-text-primary">{row.value ?? '—'}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-teacher-text-tertiary mt-3">From metrics_daily (yesterday)</p>
            </div>
          </div>
        </>
      )}

      {/* Investor Story Tab */}
      {tab === 'investor' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-teacher-border border-l-4 border-l-purple-400">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-teacher-text-primary">Atlas Engagement vs Theta Improvement</h3>
              <span className="text-xs font-medium px-2 py-1 rounded bg-teacher-bg-secondary text-teacher-text-secondary">Correlational</span>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-green-50 rounded-lg p-5 text-center">
                <div className="text-xs font-semibold text-green-700 mb-1">Atlas-engaged users</div>
                <div className="text-3xl font-bold text-green-700">—</div>
                <div className="text-xs text-teacher-text-secondary mt-1">7-day theta delta</div>
              </div>
              <div className="bg-red-50 rounded-lg p-5 text-center">
                <div className="text-xs font-semibold text-red-700 mb-1">Non-Atlas users</div>
                <div className="text-3xl font-bold text-red-700">—</div>
                <div className="text-xs text-teacher-text-secondary mt-1">7-day theta delta</div>
              </div>
            </div>

            <p className="text-xs text-teacher-text-secondary italic">
              Data populates from weekly metrics_atlas aggregation. Requires 50+ users for meaningful comparison.
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-teacher-border border-l-4 border-l-purple-400">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-teacher-text-primary">Aha Moment vs D30 Retention</h3>
              <span className="text-xs font-medium px-2 py-1 rounded bg-teacher-bg-secondary text-teacher-text-secondary">Correlational</span>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-green-50 rounded-lg p-5 text-center">
                <div className="text-xs font-semibold text-green-700 mb-1">Reached Aha</div>
                <div className="text-3xl font-bold text-green-700">—</div>
                <div className="text-xs text-teacher-text-secondary mt-1">D30 retention</div>
              </div>
              <div className="bg-red-50 rounded-lg p-5 text-center">
                <div className="text-xs font-semibold text-red-700 mb-1">Did Not Reach Aha</div>
                <div className="text-3xl font-bold text-red-700">—</div>
                <div className="text-xs text-teacher-text-secondary mt-1">D30 retention</div>
              </div>
            </div>

            <p className="text-xs text-teacher-text-secondary italic">
              Data populates from weekly metrics_cohorts aggregation. Requires 30+ days of user data.
            </p>
          </div>

          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
            <div className="flex gap-3">
              <span className="text-lg">⚠️</span>
              <div className="text-sm text-amber-800 leading-relaxed">
                <strong>Narrative metrics disclaimer:</strong> Students who engage with Atlas may differ from those who don't in baseline motivation and activity level. These comparisons are correlational, not causal. For investor decks, segment by signup source and starting theta for more reliable comparisons.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Node Breakdown Tab */}
      {tab === 'nodes' && (
        <div className="bg-white rounded-xl border border-teacher-border overflow-hidden">
          <div className="p-4 border-b border-teacher-border">
            <h2 className="text-base font-semibold text-teacher-text-primary">Node Breakdown</h2>
            <p className="text-sm text-teacher-text-secondary mt-0.5">All atlas nodes with activity in the last 7 days</p>
          </div>

          {loading ? (
            <div className="p-8 text-center text-teacher-text-tertiary">Loading...</div>
          ) : nodes.length === 0 ? (
            <div className="p-8 text-center text-teacher-text-tertiary">No activity yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-teacher-bg border-b border-teacher-border">
                  <tr>
                    <th className="text-left px-4 py-3 text-teacher-text-secondary font-medium">Node</th>
                    <th className="text-right px-4 py-3 text-teacher-text-secondary font-medium">Triggered</th>
                    <th className="text-right px-4 py-3 text-teacher-text-secondary font-medium">Opened</th>
                    <th className="text-right px-4 py-3 text-teacher-text-secondary font-medium">Completed</th>
                    <th className="text-right px-4 py-3 text-teacher-text-secondary font-medium">Skipped</th>
                    <th className="text-right px-4 py-3 text-teacher-text-secondary font-medium">Open Rate</th>
                    <th className="text-right px-4 py-3 text-teacher-text-secondary font-medium">Completion Rate</th>
                    <th className="text-right px-4 py-3 text-teacher-text-secondary font-medium">Retrieval Pass</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {nodes.map((node) => (
                    <tr key={node.nodeId} className="hover:bg-teacher-bg transition-colors">
                      <td className="px-4 py-3 text-teacher-text-primary font-medium">
                        <div>{node.nodeName}</div>
                        <div className="text-xs text-teacher-text-tertiary font-normal">{node.nodeId}</div>
                      </td>
                      <td className="px-4 py-3 text-right text-teacher-text-primary">{node.triggered}</td>
                      <td className="px-4 py-3 text-right text-teacher-text-primary">{node.opened}</td>
                      <td className="px-4 py-3 text-right text-teacher-text-primary">{node.completed}</td>
                      <td className="px-4 py-3 text-right text-teacher-text-primary">{node.skipped}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-medium ${node.openRate >= 50 ? 'text-green-600' : node.openRate >= 25 ? 'text-amber-600' : 'text-red-500'}`}>
                          {node.openRate}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-medium ${node.completionRate >= 60 ? 'text-green-600' : node.completionRate >= 30 ? 'text-amber-600' : 'text-red-500'}`}>
                          {node.opened > 0 ? `${node.completionRate}%` : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {node.retrievalAttempts > 0 ? (
                          <span className={`font-medium ${node.retrievalPassRate >= 67 ? 'text-green-600' : node.retrievalPassRate >= 40 ? 'text-amber-600' : 'text-red-500'}`}>
                            {node.retrievalPassRate}%
                            <span className="text-xs text-teacher-text-tertiary font-normal ml-1">({node.retrievalAttempts})</span>
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {data && (
        <p className="text-xs text-teacher-text-tertiary mt-4">
          Last updated: {new Date(data.generatedAt).toLocaleString()}
        </p>
      )}
    </div>
  );
}
